/*
 * Weekly view counts.
 *
 * Two jobs, both strictly decorative:
 *   - on a listing page, fire one beacon so the view is counted
 *   - on the grid, fetch counts for the cards on screen in ONE request
 *
 * Every path fails silently to hidden. A view count is the least important
 * thing on the page: if the Worker is down, slow, or blocked by an extension,
 * the visitor should see the listing exactly as they would have anyway. There
 * is no error state, no retry, and nothing here can delay render - the script
 * is deferred and every request is fire-and-forget.
 */
(function () {
  'use strict';

  /*
   * Below this, a count is noise rather than signal - "3 views this week"
   * makes a listing look ignored, and on a small sample it is not a number
   * worth showing anyone. Kept in one place so it is one edit to change.
   */
  var MIN_VIEWS = 5;

  function endpointBase(root) {
    var ep = root && root.dataset ? root.dataset.viewsEndpoint : '';
    return ep ? ep.replace(/\/+$/, '') : '';
  }

  var EYE = '\u{1F441}';

  function longLabel(n) {
    return EYE + ' ' + n.toLocaleString() + ' views this week';
  }

  /*
   * Grid cards say "N views", not "N views this week".
   *
   * A card is the narrowest place this pill appears, and it sits alongside the
   * offer and question pills, so the window is dropped from the visible text
   * and carried by `title` and `aria-label` instead. Nothing is lost: the
   * number has always been a 7-day figure, and both the tooltip and the
   * accessible name still say so.
   */
  function gridLabel(n) {
    return EYE + ' ' + n.toLocaleString() + ' views';
  }

  function shortLabel(n) {
    return EYE + ' ' + n.toLocaleString() + ' this week';
  }

  /**
   * Does this pill's row still fit inside the box it is positioned against?
   *
   * Detail page only. Grid cards no longer measure or shorten - "N views" fits
   * the narrowest card, so there is nothing left for this to catch there.
   *
   * The row is absolutely positioned over the photo, so it does not push
   * anything - it would simply run past the edge. Comparing the row's right
   * edge against its offset parent catches that, and because the pills are
   * `white-space: nowrap` the number can never be broken mid-digit.
   */
  function overflows(el) {
    var row = el.parentElement;
    if (!row) return false;
    var box = row.offsetParent || row.parentElement;
    if (!box) return false;
    var r = row.getBoundingClientRect();
    var b = box.getBoundingClientRect();
    // Half a pixel of slack: sub-pixel layout should not trigger a reword.
    return r.right > b.right - 0.5 || r.width > b.width;
  }

  /**
   * Reveal a slot's pill, but only when the number clears the threshold.
   *
   * `variant` is 'grid' for a card and 'detail' for the listing page. The
   * accessible name is the long form in both - the shorter visible text is a
   * space-saving measure for sighted readers and should not cost a screen
   * reader the word "views" or the window the number covers.
   */
  function paint(el, n, variant) {
    if (!el) return;
    var row = el.parentElement;

    if (typeof n !== 'number' || !isFinite(n) || n < MIN_VIEWS) {
      el.hidden = true;
      // If the row exists only for this pill, hide the row with it.
      if (row && row.hasAttribute('data-counts-row')) {
        var others = row.querySelectorAll('.listing-counts__pill:not([data-views-line])');
        if (others.length === 0) row.hidden = true;
      }
      return;
    }

    var full = n.toLocaleString() + ' views this week';
    el.setAttribute('aria-label', full);
    el.hidden = false;
    if (row && row.hasAttribute('data-counts-row')) row.hidden = false;

    if (variant === 'grid') {
      // Short enough for any card, so there is nothing to measure and nothing
      // to shorten. `title` keeps the window available on hover.
      el.setAttribute('title', full);
      el.textContent = gridLabel(n);
      return;
    }

    el.textContent = longLabel(n);
    // Measure once laid out, then shorten only if it genuinely does not fit.
    if (overflows(el)) el.textContent = shortLabel(n);
  }

  /**
   * Re-pick which "More from the yard sale" cards are shown.
   *
   * The row renders a POOL of candidates and shows the first N. The brief
   * asks to prefer listings whose 7-day count cleared the threshold, but that
   * number is not knowable when the page is built - it lives in D1 - and
   * fetching it server-side would mean an extra Worker call. So the
   * preference is applied here instead, using counts that were already on
   * their way for the pills.
   *
   * N never changes, so nothing moves: the same number of cards fill the same
   * space before and after, only their contents may differ. With no JS, or a
   * blocked Worker, the server's first N simply stay.
   */
  function chooseMore(views) {
    var grid = document.querySelector('[data-more-grid]');
    if (!grid) return;

    var cards = [].slice.call(grid.querySelectorAll('[data-more-card]'));
    if (cards.length === 0) return;

    var want = parseInt(grid.getAttribute('data-more-visible'), 10);
    if (!isFinite(want) || want < 0) want = 0;
    if (want > cards.length) want = cards.length;
    // Nothing to choose between: every candidate is already shown.
    if (want === 0 || want === cards.length) return;

    var qualified = [];
    var rest = [];
    cards.forEach(function (card) {
      var n = views[card.getAttribute('data-more-slot')];
      if (typeof n === 'number' && isFinite(n) && n >= MIN_VIEWS) qualified.push(card);
      else rest.push(card);
    });

    // Server order is preserved inside each group, so the row stays random.
    var show = qualified.concat(rest).slice(0, want);
    cards.forEach(function (card) {
      card.hidden = show.indexOf(card) === -1;
    });
  }

  // ---- detail page: count this view ------------------------------------
  function beacon(root) {
    var base = endpointBase(root);
    var slot = root.dataset.viewsSlot;
    if (!base || !slot) return;

    // keepalive so the request survives the visitor navigating straight on.
    try {
      fetch(base + '/view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot_id: slot }),
        keepalive: true
      }).catch(function () {
        /* counting is best-effort; never surface this */
      });
    } catch (err) {
      /* older browsers without keepalive support, or a blocked request */
    }
  }

  // ---- read counts for one or many slots --------------------------------
  function load(root, slots) {
    var base = endpointBase(root);
    if (!base || slots.length === 0) return Promise.resolve({});
    var url = base + '/views?slots=' + encodeURIComponent(slots.join(','));
    return fetch(url, { method: 'GET' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        return data && data.ok && data.views ? data.views : {};
      })
      .catch(function () {
        return {}; // stays hidden
      });
  }

  function boot() {
    var root = document.querySelector('[data-views-root]');
    if (!root) return;

    /*
     * Detail page: one beacon for this listing, then ONE batched request for
     * every pill on the page - its own, plus any "more from the yard sale"
     * cards. It used to ask for a single slot, which meant the cards' pills
     * were rendered and then never populated.
     */
    if (root.dataset.viewsSlot) {
      if (root.dataset.viewsBeacon === 'true') beacon(root);
      var slot = root.dataset.viewsSlot;

      var pills = [].slice.call(document.querySelectorAll('[data-views-line][data-views-for]'));
      var onPage = {};
      pills.forEach(function (el) {
        var s = el.dataset.viewsFor;
        if (!s) return;
        (onPage[s] = onPage[s] || []).push(el);
      });
      if (!onPage[slot]) onPage[slot] = [];

      load(root, Object.keys(onPage).slice(0, 40)).then(function (views) {
        Object.keys(onPage).forEach(function (s) {
          onPage[s].forEach(function (el) {
            // A card in the "more" row is a card; this page's own pill is not.
            var inRow = el.closest && el.closest('[data-more-grid]');
            paint(el, views[s], inRow ? 'grid' : 'detail');
          });
        });
        chooseMore(views);
      });
      return;
    }

    /*
     * Grid: one batched request for every card on screen. The Worker caps a
     * batch at 40, so longer grids are chunked rather than truncated - a
     * listing further down the page should not silently lose its count.
     */
    var lines = [].slice.call(document.querySelectorAll('[data-views-line][data-views-for]'));
    if (lines.length === 0) return;

    var bySlot = {};
    lines.forEach(function (el) {
      var s = el.dataset.viewsFor;
      if (!s) return;
      (bySlot[s] = bySlot[s] || []).push(el);
    });

    var slots = Object.keys(bySlot);
    var chunks = [];
    for (var i = 0; i < slots.length; i += 40) chunks.push(slots.slice(i, i + 40));

    chunks.forEach(function (chunk) {
      load(root, chunk).then(function (views) {
        chunk.forEach(function (slot) {
          (bySlot[slot] || []).forEach(function (el) {
            paint(el, views[slot], 'grid');
          });
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
